import { clsx } from '@alexis/helpers/clsx';
import React, { ReactNode, useRef } from 'react';
import { useSelector } from 'react-redux';

import ChevronDown from '@icons/chevron_down_small.svg';
import ClearIcon from '@icons/close.svg';

import { getIsRtlState } from '@redux/selectors';

import styles from './FilterButton.module.scss';

type FilterButtonProps = {
  children: ReactNode | string;
  isDropdownOpen: boolean;
  isFilterApplied?: boolean;
  onDropdownButtonClick: (top: number, offset: number) => void;
  onClearButtonClick?: () => void;
};

const FILTER_DROPDOWN_POPUP_WIDTH = 300; //300 is the width of the dropdown popup

const FilterButton: React.FC<FilterButtonProps> = ({
  children,
  isDropdownOpen,
  isFilterApplied,
  onClearButtonClick,
  onDropdownButtonClick,
}) => {
  const isRtl = useSelector(getIsRtlState);

  const dropdownRef = useRef<HTMLButtonElement>(null);

  const handleClearIconClick = (e: React.MouseEvent<SVGElement>) => {
    e.stopPropagation();

    if (onClearButtonClick) {
      onClearButtonClick();
    }
  };

  const handleDropdownButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    // If the dropdown popup overflow the container, adjust the right & left attribute to make it visible
    if (dropdownRef.current && dropdownRef.current.parentElement?.parentElement) {
      let offset = 0;

      const buttonPosition = dropdownRef.current.getBoundingClientRect();
      const carouselPosition =
        dropdownRef.current.parentElement.parentElement.getBoundingClientRect();

      if (isRtl) {
        if (buttonPosition.x + buttonPosition.width > carouselPosition.x + carouselPosition.width) {
          offset = 0;
        } else if (buttonPosition.x < carouselPosition.x) {
          offset = carouselPosition.width - FILTER_DROPDOWN_POPUP_WIDTH;
        } else {
          offset =
            carouselPosition.x + carouselPosition.width - (buttonPosition.x + buttonPosition.width);
        }
      } else {
        if (buttonPosition.x < carouselPosition.x) {
          offset = 0;
        } else if (
          buttonPosition.x - carouselPosition.x + FILTER_DROPDOWN_POPUP_WIDTH >
          carouselPosition.width
        ) {
          offset = carouselPosition.width - FILTER_DROPDOWN_POPUP_WIDTH;
        } else {
          offset = buttonPosition.x - carouselPosition.x;
        }
      }

      onDropdownButtonClick(dropdownRef.current.offsetTop + 42, offset);
    }
  };

  return (
    <button
      ref={dropdownRef}
      className={clsx(styles.button, (isFilterApplied || isDropdownOpen) && styles.active)}
      onClick={(e) => handleDropdownButtonClick(e)}
    >
      <div className={styles.label}>{children}</div>

      {isFilterApplied && onClearButtonClick ? (
        <ClearIcon
          data-testid='clear-icon'
          className={styles.clearIcon}
          onClick={(e) => handleClearIconClick(e)}
        />
      ) : (
        <ChevronDown className={styles.chevron} />
      )}
    </button>
  );
};

export default FilterButton;
